import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgbActiveModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { NgxMaskDirective } from 'ngx-mask';

import { CustomerUpdateRequest } from '../../../../../../core/models/customer.model';
import { DiscountTier } from '../../../../../../core/models/fee-configuration.model';
import { UserRole } from '../../../../../../core/models/user.model';
import { AuthService } from '../../../../../../core/services/auth.service';
import { FeeConfigurationApiService } from '../../../../../../core/services/api/fee-configuration-api.service';
import { CustomerService } from '../../../../../../core/services/customer.service';
import { ConfirmationService } from '../../../../../../shared';
import { Modal } from '../../../../../../shared/components/modal/modal';

export interface StatusOption {
  value: string;
  labelKey: string;
}

@Component({
  selector: 'app-customer-data-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslocoPipe, NgSelectModule, NgxMaskDirective, Modal, NgbTooltip],
  templateUrl: './customer-data-modal.html',
  styleUrl: './customer-data-modal.scss',
})
export class CustomerDataModal implements OnInit {
  private readonly formBuilder = inject(FormBuilder);

  private readonly activeModal = inject(NgbActiveModal);

  private readonly customerService = inject(CustomerService);

  private readonly authService = inject(AuthService);

  private readonly feeApiService = inject(FeeConfigurationApiService);

  private readonly transloco = inject(TranslocoService);

  private readonly confirmationService = inject(ConfirmationService);

  /**
   * Recebe o ID por meio de:
   *
   * componentInstance.customerId.set(id)
   */
  readonly customerId = signal('');

  readonly errorMessage = signal('');

  readonly statusAlterado = signal(false);

  readonly isLoading = this.customerService.isLoading;

  readonly isAdmin = computed(() => this.authService.currentUser()?.role === UserRole.Admin);

  readonly isDefaultCustomer = signal(false);

  readonly isSuper = computed(() => this.authService.isSuper());

  // O backend recusa a troca com Client.DefaultCnpjCannotBeChanged; aqui o campo nem abre para edição.
  readonly isCnpjLocked = computed(() => this.isDefaultCustomer() && !this.isSuper());

  readonly MAX_NAME_LENGTH = 100;

  readonly MAX_EMAIL_LENGTH = 50;

  readonly activeTiers = signal<DiscountTier[]>([]);

  private initialIsActive = true;

  private loadedCustomerId = '';

  readonly customerForm: FormGroup = this.formBuilder.group({
    nome: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(this.MAX_NAME_LENGTH)]],

    email: ['', [Validators.required, Validators.email, Validators.maxLength(this.MAX_EMAIL_LENGTH)]],

    cnpj: [
      '',
      [Validators.required, Validators.pattern(/^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/), Validators.maxLength(18)],
    ],

    telefone: ['', [Validators.pattern(/^\(\d{2}\) \d{5}-\d{4}$/), Validators.maxLength(15)]],

    isActive: ['true'],

    contractedTierId: [null],
  });

  readonly statusOptions: StatusOption[] = [
    {
      value: 'true',
      labelKey: 'shared.status.active',
    },
    {
      value: 'false',
      labelKey: 'shared.status.inactive',
    },
  ];

  constructor() {
    effect(() => {
      const clientId = this.customerId().trim();

      if (!clientId || clientId === this.loadedCustomerId) {
        return;
      }

      this.loadedCustomerId = clientId;

      this.loadCustomer(clientId).catch(() => {
        // Erros de carregamento são exibidos via errorMessage signal.
      });
    });
  }

  ngOnInit(): void {
    if (this.isSuper()) {
      this.feeApiService
        .getSnapshot()
        .then((snapshot) => this.activeTiers.set(snapshot.tiers.filter((t) => t.isActive)));
    }
  }

  private async loadCustomer(clientId: string): Promise<void> {
    this.errorMessage.set('');

    const customer = await this.customerService.getCustomerById(clientId);

    if (!customer) {
      this.errorMessage.set(this.transloco.translate('admin.customers.edit.errors.loadCompanyData'));

      return;
    }

    const isActive = customer.isActive ?? true;

    this.initialIsActive = isActive;

    this.isDefaultCustomer.set(customer.isDefault ?? false);

    this.customerForm.patchValue({
      nome: customer.nome ?? '',
      email: customer.email ?? '',
      cnpj: customer.cnpj ?? '',
      telefone: customer.telefone ?? '',
      isActive: String(isActive),
      contractedTierId: customer.contractedTierId ?? null,
    });

    this.customerForm.markAsPristine();
    this.customerForm.markAsUntouched();

    this.statusAlterado.set(false);
  }

  async onSubmit(): Promise<void> {
    this.errorMessage.set('');

    if (this.customerForm.invalid) {
      this.customerForm.markAllAsTouched();

      this.errorMessage.set(this.transloco.translate('validation.checkFields'));

      return;
    }

    const clientId = this.customerId().trim();

    if (!clientId) {
      this.errorMessage.set(this.transloco.translate('admin.customers.edit.errors.identifyClient'));

      return;
    }

    const formValue = this.customerForm.getRawValue();

    const request: CustomerUpdateRequest = {
      nome: formValue.nome?.trim() ?? '',

      email: formValue.email?.trim() ?? '',

      cnpj: formValue.cnpj?.trim() ?? '',

      telefone: formValue.telefone?.trim() ?? '',

      isActive: this.isAdmin() ? this.initialIsActive : formValue.isActive === 'true',

      ...(this.isSuper() && { contractedTierId: formValue.contractedTierId || null }),
    };

    const result = await this.customerService.updateCustomer(clientId, request);

    if (!result.success) {
      this.errorMessage.set(result.error ?? this.transloco.translate('admin.customers.edit.errors.updateCompanyData'));

      return;
    }

    /*
     * TG-503: devolve o nome atualizado para que o modal
     * pai sincronize a pré-visualização do logotipo.
     */
    this.activeModal.close({
      updated: true,
      customerId: clientId,
      nome: request.nome,
    });

    /*
     * TG-503: o aviso de sucesso só pode ser aberto depois que o
     * modal atual terminar de fechar, senão o ng-bootstrap descarta
     * a nova instância. Mesmo padrão dos modais de tema e logotipo.
     */
    await new Promise((resolve) => setTimeout(resolve, 200));

    /*
     * TG-503: o Admin só edita a própria empresa, então para ele o aviso
     * fala em "sua empresa". O Super edita qualquer cliente e continua
     * vendo a mensagem em terceira pessoa.
     */
    const messageKey = this.isAdmin() ? 'confirmation.companySaved' : 'confirmation.customerSaved';

    await this.confirmationService.showInfo(
      this.transloco.translate(`${messageKey}.title`),
      this.transloco.translate(`${messageKey}.message`, { name: request.nome }),
      'success',
    );
  }

  onStatusChange(): void {
    const currentStatus = this.customerForm.get('isActive')?.value === 'true';

    this.statusAlterado.set(currentStatus !== this.initialIsActive);
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.customerForm.get(fieldName);

    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  cancel(): void {
    this.activeModal.dismiss();
  }
}
